import React, { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { publicApi } from "../api";

function fmtPrice(product) {
  if (!product) return "–";
  return `$${(product.price_cents / 100).toFixed(2)} ${(product.currency || "AUD").toUpperCase()}`;
}

/* Sandbox: no card entry. Clicking Pay confirms server-side with a Stripe test
   card (Visa 4242), so testers/demos never type card details — it's still a real
   test-mode payment that shows in the Stripe dashboard / logs. */
function SandboxPanel() {
  return (
    <div className="su-testcard" role="note">
      <div className="su-testcard-head">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
        Test mode — no card details needed
      </div>
      <div className="su-testcard-note" style={{ marginTop: "0.35rem", fontSize: "0.84rem", opacity: 1 }}>
        Clicking <strong>Pay</strong> uses a Stripe <strong>test card (Visa •••• 4242)</strong> automatically — no real charge, nothing to type. It still records a test payment you can check in the logs.
      </div>
    </div>
  );
}

export default function StepPayment({ selectedProduct, sandbox, slug, paymentIntentId, onSuccess, onBack }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    // Sandbox — confirm with a test card server-side, no manual entry.
    if (sandbox) {
      try {
        const res = await publicApi.sandboxConfirmPayment(slug, paymentIntentId);
        if (res.status === "succeeded") { onSuccess(res.paymentIntentId); return; }
        setError(`Test payment returned status "${res.status}". Please try again.`);
      } catch (err) {
        setError(err.message || "Test payment failed. Please try again.");
      }
      setLoading(false);
      return;
    }

    // Live — Stripe Elements.
    if (!stripe || !elements) { setLoading(false); return; }
    const result = await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (result.error) {
      setError(result.error.message);
      setLoading(false);
    } else if (result.paymentIntent?.status === "succeeded") {
      onSuccess(result.paymentIntent.id);
    }
  };

  return (
    <>
      <h2 className="step-title">Payment</h2>
      {selectedProduct && (
        <p className="step-sub">
          {selectedProduct.name} — {fmtPrice(selectedProduct)}
        </p>
      )}

      {error && <div className="su-alert error">⚠ {error}</div>}

      <form onSubmit={handleSubmit}>
        {sandbox ? (
          <SandboxPanel />
        ) : (
          <div className="payment-wrap">
            <PaymentElement />
          </div>
        )}
        <div className="su-btn-row">
          <button
            type="button"
            className="su-btn su-btn-outline"
            onClick={onBack}
            disabled={loading}
          >
            ← Back
          </button>
          <button
            type="submit"
            className="su-btn su-btn-primary"
            disabled={loading || (!sandbox && !stripe) || (sandbox && !paymentIntentId)}
          >
            {loading
              ? "Processing…"
              : sandbox
                ? `Pay ${fmtPrice(selectedProduct)} (test)`
                : `Pay ${selectedProduct ? fmtPrice(selectedProduct) : ""}`}
          </button>
        </div>
      </form>
    </>
  );
}
